import { useParams, Link } from "react-router-dom";
import { useAwardedItems } from "@/hooks/useAwardedItems";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { motion } from "motion/react";

const sectionVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const },
  }),
};

export default function AwardedInsightDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { items: awardedItems, loading } = useAwardedItems();
  const item = awardedItems.find((a) => a.id === id);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 font-['Noto_Sans_KR']">
        <Navbar />
        <main className="pt-24 pb-16 px-4 md:px-6 text-center">
          <div className="w-8 h-8 mx-auto border-2 border-[#E8E5E0] border-t-[#C4956A] rounded-full animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-background-50 font-['Noto_Sans_KR']">
        <Navbar />
        <main className="pt-24 pb-16 px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-[600px] mx-auto text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#C4956A]/10 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-[#C4956A]"></i>
            </div>
            <p className="text-foreground-700 font-medium mb-1">해당 낙찰 사례를 찾을 수 없습니다</p>
            <p className="text-sm text-[#8C8780] mb-6">삭제되었거나 존재하지 않는 데이터입니다</p>
            <Link
              to="/awarded-insights"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1C1917] text-white text-sm font-medium rounded-full hover:bg-[#C4956A] transition-all"
            >
              <i className="ri-arrow-left-line"></i>
              낙찰인사이트 목록으로
            </Link>
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto">
          {/* Breadcrumb */}
          <motion.nav
            custom={0}
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
            className="flex items-center gap-2 text-xs text-[#8C8780] mb-6"
          >
            <Link to="/" className="hover:text-[#C4956A] transition-colors">홈</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/awarded-insights" className="hover:text-[#C4956A] transition-colors">낙찰인사이트</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">{item.caseNumber}</span>
          </motion.nav>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Column */}
            <div className="lg:w-3/5">
              {/* Image */}
              <motion.div
                custom={0.05}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                className="relative rounded-2xl overflow-hidden h-[300px] md:h-[400px] mb-6"
              >
                <motion.img loading="lazy" decoding="async"
                  initial={{ scale: 1.05 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.7, ease: "easeOut" as const }}
                  src={item.imageUrl}
                  alt={`${item.propertyType} - ${item.location}`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span className={`px-3 py-1.5 text-sm font-semibold rounded-full ${item.expertGrade === "적극추천" ? "bg-emerald-500 text-white" : item.expertGrade === "추천" ? "bg-amber-500 text-white" : "bg-gray-400 text-white"}`}>
                    {item.expertGrade}
                  </span>
                  <span className="px-3 py-1.5 text-sm font-medium bg-white/90 rounded-full text-[#1C1917]">
                    응찰 {item.bidderCount}명
                  </span>
                </div>
                <div className="absolute bottom-4 left-4">
                  <span className="px-4 py-2 text-sm font-bold bg-[#1C1917]/85 backdrop-blur-sm text-white rounded-full">
                    낙찰가 {item.actualWinningBid.toLocaleString()}원
                  </span>
                </div>
              </motion.div>

              {/* Expert Full Analysis */}
              {item.expertFullAnalysis && (
              <motion.div
                custom={0.1}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                className="bg-white rounded-xl border border-[#E8E5E0] p-6 md:p-8 mb-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-[#C4956A]/10 flex items-center justify-center">
                    <i className="ri-file-search-line text-[#C4956A]"></i>
                  </div>
                  <h2 className="text-lg font-bold text-foreground-950">전문가 심층 분석</h2>
                </div>
                <div className="bg-[#FAFAF8] rounded-lg p-5 border border-[#E8E5E0]">
                  {item.expertFullAnalysis.split("\n\n").map((paragraph, idx) => (
                    <p key={idx} className="text-sm text-foreground-700 leading-relaxed mb-3 last:mb-0">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </motion.div>
              )}

              {/* Profit Breakdown */}
              {item.profitBreakdown && (
              <motion.div
                custom={0.15}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                className="bg-white rounded-xl border border-[#E8E5E0] p-6 md:p-8 mb-6"
              >
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <i className="ri-money-dollar-circle-line text-emerald-600"></i>
                  </div>
                  <h2 className="text-lg font-bold text-foreground-950">수익률 분석</h2>
                </div>

                <div className="bg-[#FAFAF8] rounded-lg p-5 border border-[#E8E5E0] mb-5">
                  <div className="space-y-3">
                    {[
                      { label: "낙찰가", value: item.profitBreakdown.winningBid, isSub: false },
                      { label: "취득세 (4.6%)", value: item.profitBreakdown.acquisitionTax, isSub: true },
                      { label: "명도비", value: item.profitBreakdown.evictionCost, isSub: true },
                      { label: "리모델링비", value: item.profitBreakdown.renovationCost, isSub: true },
                      { label: "중개수수료", value: item.profitBreakdown.brokerageFee, isSub: true },
                    ].map((row, idx) => (
                      <div key={idx} className={`flex justify-between items-center py-1.5 ${idx === 0 ? "border-b border-[#E8E5E0] pb-2.5" : ""}`}>
                        <span className={`text-xs ${row.isSub ? "text-[#8C8780] pl-4" : "text-foreground-700 font-semibold"}`}>
                          {row.isSub && <span className="inline-block mr-1">└</span>}
                          {row.label}
                        </span>
                        <span className={`text-xs font-medium ${idx === 0 ? "text-[#C4956A] font-bold text-sm" : "text-foreground-700"}`}>
                          {row.value.toLocaleString()}원
                        </span>
                      </div>
                    ))}
                    <div className="border-t border-[#E8E5E0] pt-3 mt-1">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-bold text-foreground-950">총 투자금</span>
                        <span className="text-sm font-bold text-foreground-950">{item.profitBreakdown.totalCost.toLocaleString()}원</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Result Box */}
                <div className="bg-emerald-500/5 rounded-xl p-5 border border-emerald-500/20 flex flex-col sm:flex-row items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-up-circle-fill text-2xl text-emerald-600"></i>
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-xs text-[#8C8780] mb-0.5">예상 시세차익</p>
                    <p className="text-xl font-bold text-emerald-600">
                      {item.profitBreakdown.estimatedProfit.toLocaleString()}원
                      <span className="text-sm font-medium ml-2">(수익률 {item.profitBreakdown.profitRate}%)</span>
                    </p>
                    <p className="text-xs text-[#8C8780] mt-1">
                      예상 매도가 {item.profitBreakdown.estimatedMarketPrice.toLocaleString()}원 기준
                    </p>
                  </div>
                </div>
              </motion.div>
              )}

              {/* Key Lessons */}
              {item.keyLessons && item.keyLessons.length > 0 && (
              <motion.div
                custom={0.2}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                className="bg-white rounded-xl border border-[#E8E5E0] p-6 md:p-8 mb-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <i className="ri-lightbulb-line text-amber-600"></i>
                  </div>
                  <h2 className="text-lg font-bold text-foreground-950">이 사례에서 배울 점</h2>
                </div>
                <div className="space-y-3">
                  {item.keyLessons.map((lesson, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-4 bg-amber-500/5 rounded-lg border border-amber-500/10">
                      <div className="w-6 h-6 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-amber-600">{idx + 1}</span>
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed">{lesson}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
              )}

              {/* Regional Context */}
              {item.regionalContext && (
              <motion.div
                custom={0.25}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                className="bg-white rounded-xl border border-[#E8E5E0] p-6 md:p-8"
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-violet-500/10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-violet-600"></i>
                  </div>
                  <h2 className="text-lg font-bold text-foreground-950">지역 시장 분석</h2>
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {item.regionalContext}
                </p>

                {/* Similar Current Listings */}
                {item.similarCurrentListings && item.similarCurrentListings.length > 0 && (
                  <div className="mt-5 pt-5 border-t border-[#F0EDE8]">
                    <p className="text-sm font-bold text-foreground-950 mb-3">
                      현재 비슷한 물건이 진행 중이에요
                    </p>
                    <div className="space-y-2">
                      {item.similarCurrentListings.map((listing, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-[#FAFAF8] rounded-lg border border-[#E8E5E0]">
                          <div>
                            <p className="text-xs font-semibold text-foreground-950">{listing.location}</p>
                            <p className="text-xs text-[#8C8780]">입찰일: {listing.auctionDate}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs font-bold text-[#C4956A]">최저가 {listing.minimumPrice.toLocaleString()}원</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Link
                      to="/auctions"
                      className="mt-3 flex items-center gap-2 text-xs font-medium text-[#C4956A] hover:text-[#B08050] transition-colors"
                    >
                      현재 경매 물건 전체 보기
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                )}
              </motion.div>
              )}
            </div>

            {/* Right Column - Sticky Sidebar */}
            <div className="lg:w-2/5">
              <div className="lg:sticky lg:top-24 space-y-5">
                {/* Summary Card */}
                <motion.div
                  custom={0.08}
                  variants={sectionVariants}
                  initial="hidden"
                  animate="visible"
                  className="bg-white rounded-xl border border-[#E8E5E0] p-6 md:p-8"
                >
                  <h1 className="text-xl font-bold text-foreground-950 mb-1">
                    {item.location}
                  </h1>
                  <p className="text-sm text-[#8C8780] mb-6">
                    {item.propertyType} · {item.court} · {item.exclusiveArea}㎡ · {item.buildingAge}년차
                  </p>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between items-center py-2 border-b border-[#F0EDE8]">
                      <span className="text-sm text-[#8C8780]">사건번호</span>
                      <span className="text-sm font-semibold text-foreground-950">{item.caseNumber}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-[#F0EDE8]">
                      <span className="text-sm text-[#8C8780]">감정가</span>
                      <span className="text-sm font-semibold text-foreground-950">{item.appraisedValue.toLocaleString()}원</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-[#F0EDE8]">
                      <span className="text-sm text-[#8C8780]">최저가</span>
                      <span className="text-sm font-semibold text-foreground-700">{item.minimumPrice.toLocaleString()}원</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-[#F0EDE8]">
                      <span className="text-sm text-[#8C8780]">낙찰가</span>
                      <span className="text-lg font-bold text-[#C4956A]">{item.actualWinningBid.toLocaleString()}원</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-[#F0EDE8]">
                      <span className="text-sm text-[#8C8780]">응찰자 수</span>
                      <span className="text-sm font-bold text-foreground-950">{item.bidderCount}명 (경쟁률 {item.bidCompetitionRate})</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-[#8C8780]">낙찰일</span>
                      <span className="text-sm font-semibold text-foreground-950">{item.auctionDate}</span>
                    </div>
                  </div>

                  {/* Grade Badge */}
                  <div className={`p-4 rounded-lg mb-6 ${item.expertGrade === "적극추천" ? "bg-emerald-500/5 border border-emerald-500/20" : item.expertGrade === "추천" ? "bg-amber-500/5 border border-amber-500/20" : "bg-gray-100 border border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <i className={`text-lg ${item.expertGrade === "적극추천" ? "ri-star-fill text-emerald-500" : item.expertGrade === "추천" ? "ri-star-fill text-amber-500" : "ri-star-line text-gray-400"}`}></i>
                      <span className="text-sm font-bold text-foreground-950">강스터디 평가: {item.expertGrade}</span>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed">{item.expertSummary}</p>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <Link
                      to="/bid-calculator"
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#C4956A] text-white rounded-full text-sm font-semibold hover:bg-[#B08050] transition-all whitespace-nowrap inline-flex"
                    >
                      <i className="ri-calculator-line"></i>
                      입찰계산기로 분석해보기
                    </Link>
                    <Link
                      to="/investment-simulator"
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#E8E5E0] text-foreground-700 rounded-full text-sm font-medium hover:border-[#C4956A] hover:text-[#C4956A] transition-all whitespace-nowrap inline-flex"
                    >
                      <i className="ri-robot-2-line"></i>
                      AI 투자 시뮬레이터
                    </Link>
                    <Link
                      to="/courses"
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#E8E5E0] text-foreground-700 rounded-full text-sm font-medium hover:border-[#C4956A] hover:text-[#C4956A] transition-all whitespace-nowrap inline-flex"
                    >
                      <i className="ri-graduation-cap-line"></i>
                      이 방식 배우러 가기
                    </Link>
                    <a
                      href="/awarded-insights"
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#E8E5E0] text-foreground-700 rounded-full text-sm font-medium hover:border-[#C4956A] hover:text-[#C4956A] transition-all whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line"></i>
                      다른 낙찰 사례 보기
                    </a>
                  </div>
                </motion.div>

                {/* Tip Card */}
                <motion.div
                  custom={0.12}
                  variants={sectionVariants}
                  initial="hidden"
                  animate="visible"
                  className="bg-white rounded-xl border border-[#E8E5E0] p-6"
                >
                  <h3 className="text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                    <i className="ri-information-line text-[#C4956A]"></i>
                    낙찰인사이트 활용법
                  </h3>
                  <ul className="space-y-2 text-xs text-foreground-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-emerald-500 mt-0.5"></i>
                      <span>사례별 <strong className="text-foreground-950">전문가 분석</strong>을 읽으며 분석 안목을 키우세요</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-emerald-500 mt-0.5"></i>
                      <span><strong className="text-foreground-950">입찰계산기</strong>에 직접 값을 넣어보며 낙찰가의 적정성을 판단해보세요</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-emerald-500 mt-0.5"></i>
                      <span>같은 지역 <strong className="text-foreground-950">현재 진행 중인 물건</strong>과 비교하며 투자 기회를 발견하세요</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-emerald-500 mt-0.5"></i>
                      <span>데이터는 <strong className="text-foreground-950">매주 업데이트</strong>됩니다. 꾸준히 방문해 주세요</span>
                    </li>
                  </ul>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA: 교육 연계 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ delay: 0.3, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] as const }}
          className="max-w-[1280px] mx-auto mt-16"
        >
          <div className="bg-gradient-to-r from-[#1C1917] to-[#3D2315] rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-xl md:text-2xl font-bold text-white mb-3">
                이 사례, 강의에서 직접 분석해 드립니다
              </h2>
              <p className="text-sm text-white/70 leading-relaxed mb-5 max-w-[500px]">
                낙찰물건 인사이트의 모든 사례는 강스터디 교육과정의 실전 교보재로 활용됩니다.
                강사님과 함께 이 물건을 처음부터 끝까지 분석해보고,
                다음 경매에서는 직접 판단할 수 있는 힘을 길러보세요.
              </p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <Link
                  to="/courses"
                  className="px-6 py-3 bg-white text-[#1C1917] font-semibold rounded-full hover:bg-[#C4956A] hover:text-white transition-all text-sm whitespace-nowrap inline-flex items-center gap-2"
                >
                  <i className="ri-book-open-line"></i>
                  교육과정 보기
                </Link>
                <Link
                  to="/lectures"
                  className="px-6 py-3 bg-white/10 text-white border border-white/20 font-medium rounded-full hover:bg-white/20 transition-all text-sm whitespace-nowrap inline-flex items-center gap-2"
                >
                  <i className="ri-play-circle-line"></i>
                  무료 강의 먼저 듣기
                </Link>
              </div>
            </div>
            <div className="w-full md:w-[200px] flex-shrink-0 text-center">
              <div className="w-20 h-20 mx-auto rounded-full bg-white/10 flex items-center justify-center mb-3">
                <i className="ri-user-star-line text-3xl text-[#C4956A]"></i>
              </div>
              <p className="text-white font-bold text-sm">강현우 강사</p>
              <p className="text-white/50 text-xs">15년 경력 · 500건+ 컨설팅</p>
            </div>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}